import { Component, OnInit, ViewChild } from '@angular/core'
import {
  ProviderODataService,
  IProvider,
  ProviderStateODataService,
  Permissions
} from '@exchange/domain/.'
import {
  ActionButtonsColumnRendererComponent,
  BooleanFilterComponent,
  ConfirmModalComponent,
  GridWrapperComponent,
  LookupFilterComponent,
  Option
} from 'common-components'
import { ActivatedRoute, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'
import {
  BasicUserPermissionsComponentModel,
  selectBasicUserPermissionsComponentModel
} from '@exchange/app/store/selectors/selectors'
import { select, Store } from '@ngrx/store'
import { AppState } from '@exchange/app/store/models/app-state'
import { takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { ColDef } from 'ag-grid-community'
import { BsModalService } from 'ngx-bootstrap/modal'
import { DateTimeColumnRendererComponent } from '../../shared/controls/grid-renderer/date-time-column.renderer.component'

@Component({
  selector: 'app-providers-list',
  templateUrl: './providers-list.component.html',
  standalone: false
})
export class ProvidersListComponent implements OnInit {
  model: IProvider = null
  states: Option[] = []

  @ViewChild('grid', { static: true }) grid: GridWrapperComponent
  componentModel: BasicUserPermissionsComponentModel
  ngUnsubscribe: Subject<boolean> = new Subject()

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private odata: ProviderODataService,
    private stateService: ProviderStateODataService,
    private toastr: ToastrService,
    private store: Store<AppState>,
    private modalService: BsModalService
  ) {
    this.store
      .pipe(select(selectBasicUserPermissionsComponentModel), takeUntil(this.ngUnsubscribe))
      .subscribe(m => {
        this.componentModel = m
      })
  }

  private initializeStates(): void {
    this.stateService
      .query()
      .orderBy('Name')
      .exec()
      .subscribe(response => {
        response.data.forEach(x =>
          this.states.push(new Option(x.Id, `${x.Abbreviation} - (${x.Name})`, x))
        )
        this.grid.setLookupFilterOptions('PhysicalAddress.StateId', this.states)
      })
  }

  ngOnInit() {
    this.initializeStates()
    this.initializeGrid()

    const sm = this.route.snapshot.queryParams['successMessage']
    if (sm) {
      this.toastr.success(sm)
    }
  }

  addEntity(): void {
    this.router.navigate(['/system', 'providers', 0])
  }

  editClicked(data) {
    const model = data as IProvider
    this.router.navigate(['/system', 'providers', model.Id])
  }

  deleteClicked(data) {
    this.model = data as IProvider
    this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: 'Delete Provider?',
          message: 'Are you sure you want to delete the selected Provider?'
        }
      })
      .content.onClose.subscribe(userConfirm => userConfirm && this.deleteModel())
  }

  deleteModel(): void {
    this.odata
      .delete(this.model.Id)
      .exec()
      .subscribe(response => this.grid.getData())
  }

  hasSystemManagementPermission(): boolean {
    return this.componentModel.userPermissions.includes(Permissions.SystemManagement)
  }

  initializeGrid() {
    this.grid.defaultExpand =
      'PhysicalAddress, ProviderType, PhoneNumber1, OrganizationPhoneNumberOverride1, Market, Local911PhoneNumber1'

    if (!this.hasSystemManagementPermission()) {
      this.grid.defaultFilter = 'IsAnchor eq true'
    }

    this.grid.gridOptions.context = {
      canEdit: true,
      canDelete: this.hasSystemManagementPermission(),
      editHandler: this.editClicked.bind(this),
      deleteHandler: this.deleteClicked.bind(this),
      overrideTimeZone: 'US/Eastern'
    }
    this.grid.frameworkComponents = {
      lookupFilter: LookupFilterComponent,
      boolFilter: BooleanFilterComponent
    }

    const actionCol: ColDef = {
      headerName: '',
      field: 'Actions',
      pinned: 'left',
      width: 100,
      cellRenderer: ActionButtonsColumnRendererComponent
    }

    const providerIdCol: ColDef = {
      headerName: 'CHI Provider ID',
      field: 'Id',
      width: 75,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agNumberColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const anchorCol: ColDef = {
      headerName: 'Anchor',
      field: 'IsAnchor',
      width: 100,
      cellRenderer: GridWrapperComponent.BooleanRenderer(),
      filter: 'boolFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const npiCol: ColDef = {
      headerName: 'NPI',
      field: 'Npi',
      width: 60,
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep', defaultOption: 'equals' }
    }

    const nameCol: ColDef = {
      headerName: 'Name',
      field: 'Name',
      width: 100,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const addressCol: ColDef = {
      headerName: 'Address',
      field: 'PhysicalAddress.Address1',
      width: 100,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const cityCol: ColDef = {
      headerName: 'City',
      field: 'PhysicalAddress.CityName',
      width: 100,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const stateCol: ColDef = {
      headerName: 'State',
      field: 'PhysicalAddress.StateId',
      width: 60,
      filter: 'lookupFilter',
      cellRenderer: GridWrapperComponent.LookupFieldRenderer(this.states)
    }

    const zipCol: ColDef = {
      headerName: 'Zip Code',
      field: 'PhysicalAddress.ZipCode',
      width: 60,
      cellRenderer: GridWrapperComponent.ZipcodeRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const typeCol: ColDef = {
      headerName: 'Type',
      field: 'ProviderType.Value',
      width: 60,
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const taxIdCol: ColDef = {
      headerName: 'Tax ID',
      field: 'TaxIdNumber',
      width: 60,
      cellRenderer: GridWrapperComponent.TaxIdRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const phoneCol: ColDef = {
      headerName: 'Phone Number',
      field: 'PhoneNumber1.Number',
      width: 60,
      cellRenderer: GridWrapperComponent.PhoneNumberRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const programNameOverrideCol: ColDef = {
      headerName: 'Program Name Override',
      field: 'OrganizationNameOverride',
      width: 60,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const programPhoneOverride: ColDef = {
      headerName: 'Program Phone Number Override',
      field: 'OrganizationPhoneNumberOverride1.Number',
      width: 60,
      cellRenderer: GridWrapperComponent.PhoneNumberRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const marketIdCol: ColDef = {
      headerName: 'Market ID',
      field: 'MarketId',
      width: 75,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agNumberColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const marketNameCol: ColDef = {
      headerName: 'Market Name',
      field: 'Market.Name',
      width: 100,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const local911PhoneCol: ColDef = {
      headerName: 'Local 911 Number',
      field: 'Local911PhoneNumber1.Number',
      width: 60,
      cellRenderer: GridWrapperComponent.PhoneNumberRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const lastModifiedBy: ColDef = {
      headerName: 'Last Modified By',
      field: 'ModifiedByFullName',
      width: 100,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }

    const modifiedOn: ColDef = {
      headerName: 'Modified On',
      field: 'ModifiedOn',
      width: 100,
      filter: 'agDateColumnFilter',
      cellRenderer: DateTimeColumnRendererComponent,
      filterParams: { newRowsAction: 'keep' }
    }

    if (this.hasSystemManagementPermission()) {
      this.grid.gridOptions.columnDefs = [
        actionCol,
        providerIdCol,
        anchorCol,
        npiCol,
        nameCol,
        addressCol,
        cityCol,
        stateCol,
        zipCol,
        typeCol,
        taxIdCol,
        phoneCol,
        local911PhoneCol,
        programNameOverrideCol,
        programPhoneOverride,
        lastModifiedBy,
        modifiedOn
      ]
    } else {
      this.grid.gridOptions.columnDefs = [
        actionCol,
        marketIdCol,
        marketNameCol,
        providerIdCol,
        nameCol,
        npiCol,
        taxIdCol
      ]
    }

    this.grid.odata = this.odata
  }
}
